import { useState } from "react";
import { api, createIdempotencyKey } from "../api/client";
import type { ProxyImportResult, SubConnection } from "../api/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { ConnectionDialog, ProxyImportDialog } from "../components/IntegrationDialogs";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { StatusPill } from "../components/StatusPill";
import { useRemoteResource } from "../hooks/useRemoteResource";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/format";

export function IntegrationsPage() {
  const { pushToast } = useToast();
  const connections = useRemoteResource(api.integrations.connections);
  const proxies = useRemoteResource(api.integrations.proxies);
  const logs = useRemoteResource(api.integrations.logs);
  const unsupported = connections.unsupported && proxies.unsupported;
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<SubConnection | null>(null);
  const [deleteConnection, setDeleteConnection] = useState<SubConnection | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [deletingConnection, setDeletingConnection] = useState(false);
  const [proxyImportOpen, setProxyImportOpen] = useState(false);
  const [selectedProxyIds, setSelectedProxyIds] = useState<number[]>([]);
  const [proxyDeleteOpen, setProxyDeleteOpen] = useState(false);
  const [deletingProxies, setDeletingProxies] = useState(false);
  const [actionError, setActionError] = useState("");

  function openConnection(connection?: SubConnection) {
    setEditingConnection(connection || null);
    setConnectionOpen(true);
    setActionError("");
  }

  async function testConnection(connection: SubConnection) {
    setTestingId(connection.id);
    setActionError("");
    try {
      await api.integrations.testConnection(connection.id, createIdempotencyKey("sub2-test"));
      pushToast({ tone: "success", title: `${connection.name} 连接正常`, message: "服务端已完成真实健康检查并记录检查时间。" });
      connections.reload();
      logs.reload();
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "连接测试失败";
      setActionError(message);
      pushToast({ tone: "error", title: `${connection.name} 测试失败`, message });
      logs.reload();
    } finally {
      setTestingId(null);
    }
  }

  async function confirmDeleteConnection() {
    if (!deleteConnection) return;
    setDeletingConnection(true);
    setActionError("");
    try {
      await api.integrations.deleteConnection(deleteConnection.id, createIdempotencyKey("sub2-delete"));
      pushToast({ tone: "success", title: "Sub2 连接已删除", message: `${deleteConnection.name} 不再出现在新任务选项中。` });
      setDeleteConnection(null);
      connections.reload();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "连接删除失败，请重试。");
      setDeleteConnection(null);
    } finally {
      setDeletingConnection(false);
    }
  }

  function toggleProxy(id: number) {
    setSelectedProxyIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  async function confirmDeleteProxies() {
    if (!selectedProxyIds.length) return;
    setDeletingProxies(true);
    setActionError("");
    try {
      const result = await api.integrations.deleteProxies(selectedProxyIds, createIdempotencyKey("proxy-delete"));
      pushToast({ tone: "success", title: `已删除 ${result.deleted} 条代理`, message: "后续新任务不会再使用这些代理路径。" });
      setSelectedProxyIds([]);
      setProxyDeleteOpen(false);
      proxies.reload();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "代理删除失败，请重试。");
      setProxyDeleteOpen(false);
    } finally {
      setDeletingProxies(false);
    }
  }

  function onProxyImported(result: ProxyImportResult, replaced: boolean) {
    proxies.reload();
    setSelectedProxyIds([]);
    pushToast({
      tone: result.imported ? "success" : "info",
      title: result.imported ? `已导入 ${result.imported} 条代理` : "没有代理被写入",
      message: result.imported
        ? `${replaced ? "代理池已替换" : "代理已追加"}${result.invalid.length ? `，${result.invalid.length} 行未写入` : "，没有跳过记录"}。`
        : `请检查弹窗中的 ${result.invalid.length} 行诊断后重新输入。`,
    });
  }

  return (
    <div className="page-stack">
      <section className="integration-radar">
        <div><span className="eyebrow eyebrow--light">CONNECTION MAP</span><h2>连接是任务的一部分，<br />不是页面角落里的配置。</h2><p>在这里维护 Sub2 去向和代理池，任务向导会立即读取最新可用配置。</p></div>
        <div className="radar-visual" aria-hidden="true"><span /><span /><span /><i /></div>
        <div className="integration-radar__counts"><span><b>{connections.data?.items.length ?? "—"}</b>Sub2</span><span><b>{proxies.data?.total ?? "—"}</b>代理</span></div>
      </section>

      {unsupported ? <EmptyState icon="integrations" title="当前环境未开放集成 API" description="这里不会展示模拟连接。升级后端后会自动读取 Sub2、代理池和操作日志。" /> : null}
      {actionError ? <div className="flow-error" role="alert">{actionError}</div> : null}

      <div className="integration-layout">
        <section className="integration-board">
          <div className="section-heading"><div><span className="eyebrow">DESTINATIONS</span><h2>Sub2 连接</h2></div><button className="button button--quiet button--small" onClick={() => openConnection()}>新建连接</button></div>
          {connections.loading ? <LoadingRows /> : null}
          {connections.error ? <ResourceError message={connections.error} onRetry={connections.reload} /> : null}
          {!connections.loading && !connections.error && !connections.unsupported && !connections.data?.items.length ? <EmptyState compact icon="network" title="还没有 Sub2 连接" description="创建 direct 连接并通过真实健康检查后，可在任务向导中选择。" action={<button className="button button--primary" onClick={() => openConnection()}>创建连接</button>} /> : null}
          <div className="connection-list">
            {connections.data?.items.map((connection) => (
              <article className="connection-row" key={connection.id}>
                <span className="connection-row__glyph"><Icon name="network" size={22} /></span>
                <div><span className="eyebrow">{connection.mode}</span><h3>{connection.name || `连接 #${connection.id}`}</h3><p>{connection.sub_base_url || connection.bridge_url || "未配置地址"}</p></div>
                <div className="connection-row__health"><StatusPill status={connection.status} /><small>检查于 {formatDate(connection.last_checked_at)}</small></div>
                <span className="secret-chip"><Icon name="key" size={15} />{connection.admin_key_masked || connection.bridge_token_masked || "未保存密钥"}</span>
                <div className="connection-row__actions"><button type="button" className="button button--quiet button--small" onClick={() => void testConnection(connection)} disabled={testingId !== null}>{testingId === connection.id ? <span className="button-spinner" /> : null}{testingId === connection.id ? "测试中" : "测试"}</button><button type="button" className="button button--quiet button--small" onClick={() => openConnection(connection)} aria-label={`编辑 ${connection.name}`}>编辑</button><button type="button" className="icon-button integration-delete" onClick={() => setDeleteConnection(connection)} aria-label={`删除 ${connection.name}`}>×</button></div>
              </article>
            ))}
          </div>
        </section>

        <aside className="proxy-board">
          <div className="section-heading"><div><span className="eyebrow">ROUTES</span><h2>代理池</h2></div><div className="section-heading__actions"><button className="button button--quiet button--small" onClick={() => setProxyImportOpen(true)}>批量导入</button><button className="icon-button" onClick={proxies.reload} aria-label="刷新代理"><Icon name="refresh" size={18} /></button></div></div>
          {proxies.loading ? <LoadingRows /> : null}
          {proxies.error ? <ResourceError message={proxies.error} onRetry={proxies.reload} /> : null}
          {!proxies.loading && !proxies.error && !proxies.unsupported && !proxies.data?.items.length ? <EmptyState compact icon="integrations" title="代理池为空" description="新任务默认要求使用当前用户的代理；请按行输入，系统不会静默改用服务器网络。" action={<button className="button button--primary" onClick={() => setProxyImportOpen(true)}>输入代理</button>} /> : null}
          {proxies.data?.items.length ? <div className="proxy-batch-tools"><label className="check-control"><input type="checkbox" checked={selectedProxyIds.length === proxies.data.items.length} onChange={(event) => setSelectedProxyIds(event.target.checked ? proxies.data!.items.map((proxy) => proxy.id) : [])} /><span>全选</span></label><button type="button" className="button button--danger-quiet button--small" onClick={() => setProxyDeleteOpen(true)} disabled={!selectedProxyIds.length}>删除选中 ({selectedProxyIds.length})</button></div> : null}
          <div className="proxy-list">
            {proxies.data?.items.map((proxy, index) => (
              <div key={proxy.id} className={selectedProxyIds.includes(proxy.id) ? "is-selected" : ""}><label className="proxy-select"><input type="checkbox" checked={selectedProxyIds.includes(proxy.id)} onChange={() => toggleProxy(proxy.id)} aria-label={`选择代理 ${proxy.label || proxy.id}`} /><span>{String(index + 1).padStart(2, "0")}</span></label><p><b>{proxy.masked_url}</b><small>{proxy.label || `${proxy.scheme} · ${proxy.host}:${proxy.port}`}</small></p><StatusPill status={proxy.status} /></div>
            ))}
          </div>
        </aside>
      </div>

      <section className="operation-timeline">
        <div className="section-heading"><div><span className="eyebrow">AUDIT TRAIL</span><h2>最近集成操作</h2></div><button className="icon-button" onClick={logs.reload} aria-label="刷新操作日志"><Icon name="refresh" size={18} /></button></div>
        {logs.loading ? <LoadingRows /> : null}
        {logs.error ? <ResourceError message={logs.error} onRetry={logs.reload} /> : null}
        {!logs.loading && !logs.error && !logs.unsupported && !logs.data?.items.length ? <EmptyState compact icon="clock" title="还没有操作日志" description="测试、扫描和回填发生后会在这里留下记录。" /> : null}
        <div className="timeline-list">
          {logs.data?.items.slice(0, 8).map((log) => <div key={log.id}><span className={`timeline-dot timeline-dot--${log.status === "success" ? "success" : "danger"}`} /><b>{log.operation}</b><p>{log.error || `连接 #${log.sub_connection_id || "—"}`}</p><StatusPill status={log.status} /><time>{formatDate(log.created_at)}</time></div>)}
        </div>
      </section>
      <ConnectionDialog open={connectionOpen} connection={editingConnection} onClose={() => setConnectionOpen(false)} onSaved={(connection, created) => { connections.reload(); pushToast({ tone: "success", title: created ? "Sub2 连接已创建" : "Sub2 连接已更新", message: `${connection.name} 已由服务端保存，可继续执行健康检查。` }); }} />
      <ProxyImportDialog open={proxyImportOpen} onClose={() => setProxyImportOpen(false)} onImported={onProxyImported} />
      <ConfirmDialog open={Boolean(deleteConnection)} title={`删除 ${deleteConnection?.name || "Sub2 连接"}？`} description="该连接会从后续任务选项中移除；历史任务记录不会被删除。" confirmLabel="确认删除" danger busy={deletingConnection} onCancel={() => setDeleteConnection(null)} onConfirm={() => void confirmDeleteConnection()} />
      <ConfirmDialog open={proxyDeleteOpen} title={`删除 ${selectedProxyIds.length} 条代理？`} description="所选代理会立即从活动代理池移除；已经运行中的任务不保证改变现有路径。" confirmLabel="删除选中代理" danger busy={deletingProxies} onCancel={() => setProxyDeleteOpen(false)} onConfirm={() => void confirmDeleteProxies()} />
    </div>
  );
}
